<template>
  <i
    v-if="gridEstablished"
    :class="[
      'tm-secondhand',
      {
        '-hovered': $data.isHovered,
      },
    ]"
    :style="styleObj"
  >
    <i
      class="-bubble -drag-handle"
      data-from="top"
      @mouseenter="$_toggleHoverMovable"
      @mouseleave="$_toggleHoverMovable"
      v-tm-move="moveObj"
    >
      <i class="-bubble-marker" />
    </i>

    <i
      class="-triangle -drag-handle"
      data-from="top"
      @mouseenter="$_toggleHoverMovable"
      @mouseleave="$_toggleHoverMovable"
      v-tm-move="moveObj"
    />

    <i class="-leveler-box" data-from="middle">
      <i class="-line -visual-track" :class="{ '-cut': isVisualLocked }" />
      <i v-if="shouldShow_TrackLayerTranscript" class="-line -text-track" />
      <i
        v-if="shouldShow_TrackLayerAudio"
        class="-line -audio-track"
        :class="{ '-cut': isAudioLocked }"
      />
      <i class="-line -panel-padding" />
    </i>

    <i class="-panel" :style="panelStyleObj">
      <template v-if="showOptions">
        <i
          title="Split clip"
          :class="['-button', '-visual', { e: canSplitVisual }]"
          @mouseup.left="$_onClickSplitVisual"
          @mouseenter="$_toggleHoverSplitVisualButton"
          @mouseleave="$_toggleHoverSplitVisualButton"
        >
          <i class="wticon wticon-camera2" />
        </i>
        <i
          title="Split audio"
          :class="['-button', '-audio', { e: canSplitAudio }]"
          @mouseup.left="$_onClickSplitAudio"
          @mouseenter="$_toggleHoverSplitAudioButton"
          @mouseleave="$_toggleHoverSplitAudioButton"
        >
          <i class="wticon wticon-mic" />
        </i>
      </template>
      <i
        v-else
        class="-banner -drag-handle"
        data-from="bottom"
        @mouseenter="$_toggleHoverMovable"
        @mouseleave="$_toggleHoverMovable"
        v-tm-move="moveObj"
      >
        <i class="-text" v-text="displayTime" />
      </i>

      <i class="-trailer">
        <i
          :class="[
            '-button',
            '-unix',
            {
              e: canSplitUnix,
              x: MODE_SECONDHAND_CUT,
            },
          ]"
          :title="splitUnixTitle"
          @mouseup.left="$_onClickSplitUnix"
          @mouseenter="$_toggleHoverSplitUnixButton"
          @mouseleave="$_toggleHoverSplitUnixButton"
        >
          <i v-if="MODE_SECONDHAND_CUT" class="wticon wticon-close_x" />
          <i v-else class="wticon wticon-cut" />
        </i>
      </i>
    </i>
  </i>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import gridMixin from '../grid.mixin';
import tmMove from '../movable/tmMove';
import moveSecondhandMixin from '../movable/moveSecondhand';
import { TimePoint } from '../../api/grid';
import { trackTimeFormat } from '../../api/tapi';
import { canSplitAudioAtTime, canSplitVisualAtTime } from '../../api/asset';
import tmBus from '../../api/tmBus';
import secondhandVisibilityMixin from './secondhandVisibilityMixin';

export default {
  name: 'tm-secondhand',
  mixins: [gridMixin, moveSecondhandMixin, secondhandVisibilityMixin],
  directives: { tmMove },

  data() {
    return {
      isHovered: false,
      isUnixHovered: false,
      isVisualHovered: false,
      isAudioHovered: false,
      timePoint: new TimePoint(),
    };
  },

  computed: {
    ...mapGetters('timemachine', [
      'isDndMode',
      'maxLayerDuration',
      'shouldShow_TrackLayerAudio',
      'shouldShow_TrackLayerTranscript',
    ]),
    ...mapState('timemachine', {
      secondhand: (state) => state.secondhand,
      MODE_RCP_MSG_PLAY: (state) => state.MODE.RCP_MSG_PLAY,
      MODE_SCENE_EDIT: (state) => state.MODE.SCENE_EDIT,
      MODE_OPTIONAL_AUDIO_INSERT: (state) => state.MODE.OPTIONAL_AUDIO_INSERT,
      MODE_AUDIO_MULTISELECT: (state) => state.MODE.AUDIO_MULTISELECT,
      MODE_SECONDHAND_CUT: (state) => state.MODE.SECONDHAND_CUT,
      pxSecondhandBannerW: (state) => state.cssVariables.pxSecondhandBannerW,
    }),

    point() {
      return this.$_time2point(this.secondhand, true, this.$data.timePoint);
    },

    styleObj() {
      return {
        transform: `translate3d(${this.$data.timePoint.x}px, ${this.$data.timePoint.y}px, 0)`,
      };
    },

    displayTime() {
      return trackTimeFormat(this.secondhand);
    },

    splitUnixTitle() {
      let rv = '';
      if (
        this.MODE_SECONDHAND_CUT ||
        Boolean.prototype.XNOR(this.canSplitVisual, this.canSplitAudio)
      ) {
        // no title
      } else if (this.canSplitVisual) {
        rv = 'Split clip';
      } else if (this.canSplitAudio) {
        rv = 'Split audio';
      }
      return rv;
    },

    rowBoundingBox() {
      // take first as a sample
      return {
        left: this.grid[0].p1.x,
        right: this.grid[0].p2.x,
      };
    },

    panelStyleObj() {
      const trailerW = 0;
      const leftTendril = this.pxSecondhandBannerW / 2;
      const rightTendril = this.pxSecondhandBannerW / 2 + trailerW;
      let shift = -this.pxSecondhandBannerW / 2 + 1;

      if (this.rowBoundingBox.left >= this.point.x - leftTendril) {
        shift += this.rowBoundingBox.left - (this.point.x - leftTendril) - 1;
      } else if (this.rowBoundingBox.right <= this.point.x + rightTendril) {
        shift -= this.point.x + rightTendril - this.rowBoundingBox.right;
      }

      return {
        transform: `translateX(${shift}px)`,
      };
    },

    visualForSplit() {
      return this.$_time2visualTrack(this.secondhand);
    },

    audioForSplit() {
      return this.$_time2audioTrack(this.secondhand);
    },

    canSplitVisual() {
      let rv = false;

      if (!this.isDndMode && !this.MODE_RCP_MSG_PLAY) {
        const track = this.visualForSplit;
        if (track) {
          rv = canSplitVisualAtTime(track.data, this.secondhand);
        }
      }

      return rv;
    },

    canSplitAudio() {
      const isBusy = this.isDndMode || this.MODE_RCP_MSG_PLAY;
      const hasAudio4Split = !isBusy && this.audioForSplit;
      return (
        hasAudio4Split &&
        canSplitAudioAtTime(this.audioForSplit, this.secondhand)
      );
    },

    canSplitUnix() {
      return this.canSplitAudio || this.canSplitVisual;
    },

    isVisualLocked() {
      if (this.MODE_SECONDHAND_CUT) {
        return this.canSplitVisual && this.$data.isVisualHovered;
      } else {
        return (
          this.$data.isUnixHovered &&
          this.canSplitVisual &&
          Boolean.prototype.XOR(this.canSplitVisual, this.canSplitAudio)
        );
      }
    },

    isAudioLocked() {
      if (this.MODE_SECONDHAND_CUT) {
        return this.canSplitAudio && this.$data.isAudioHovered;
      } else {
        return (
          this.$data.isUnixHovered &&
          this.canSplitAudio &&
          Boolean.prototype.XOR(this.canSplitVisual, this.canSplitAudio)
        );
      }
    },

    showOptions() {
      return this.MODE_SECONDHAND_CUT;
    },
  },

  watch: {
    maxLayerDuration() {
      if (this.secondhand > this.maxLayerDuration) {
        this.SET_SECONDHAND({ time: this.maxLayerDuration });
      }
    },

    MODE_RCP_MSG_PLAY() {
      if (this.MODE_RCP_MSG_PLAY && this.MODE_SECONDHAND_CUT) {
        this.SET_MODE({ name: 'SECONDHAND_CUT', value: false });
      }
    },
  },

  methods: {
    ...mapActions('timemachine', [
      'SPLIT_VISUAL',
      'SPLIT_AUDIO',
      'SCENE_EDIT_MODE_APPLY',
      'MANAGED_AUDIO_INSERT_EXIT',
      'SET_SECONDHAND',
      'AUDIO_MULTISELECT_EXIT',
      'SET_MODE',
    ]),

    $_toggleHoverMovable(e) {
      this.$data.isHovered = e.type === 'mouseenter';
    },

    $_toggleHoverSplitUnixButton(e) {
      this.$data.isUnixHovered = e.type === 'mouseenter';
    },

    $_toggleHoverSplitVisualButton(e) {
      this.$data.isVisualHovered = e.type === 'mouseenter';
    },

    $_toggleHoverSplitAudioButton(e) {
      this.$data.isAudioHovered = e.type === 'mouseenter';
    },

    $_onClickSplitUnix(e) {
      if (this.MODE_SECONDHAND_CUT) {
        e.stopPropagation();

        this.SET_MODE({ name: 'SECONDHAND_CUT', value: false });
      } else if (this.canSplitVisual && this.canSplitAudio) {
        e.stopPropagation();

        this.SET_MODE({ name: 'SECONDHAND_CUT', value: true });
      } else if (this.canSplitAudio) {
        e.stopPropagation();

        this.$_splitAudio(e);
      } else if (this.canSplitVisual) {
        e.stopPropagation();

        this.$_splitVisual(e);
      }
    },

    $_onClickSplitVisual(e) {
      if (this.canSplitVisual) {
        e.stopPropagation();

        this.$_splitVisual(e);
      }
    },

    $_onClickSplitAudio(e) {
      if (this.canSplitAudio) {
        e.stopPropagation();

        this.$_splitAudio(e);
      }
    },

    $_splitAudio(e) {
      this.$_beforeSplitAudio().then(() => {
        this.$_performSplitAudio(e).then(() => {
          this.$_afterSplitAny();
        });
      });
    },

    $_splitVisual(e) {
      this.$_beforeSplitVisual(e).then(() => {
        this.$_performSplitVisual(e).then(() => {
          this.$_afterSplitAny();
        });
      });
    },

    $_afterSplitAny() {
      if (
        this.MODE_SECONDHAND_CUT &&
        !this.canSplitAudio &&
        !this.canSplitVisual
      ) {
        this.SET_MODE({ name: 'SECONDHAND_CUT', value: false });
      }
    },

    $_beforeSplitAudio() {
      return new Promise((resolve) => {
        if (this.MODE_OPTIONAL_AUDIO_INSERT) {
          this.MANAGED_AUDIO_INSERT_EXIT().then(resolve);
        } else if (this.MODE_AUDIO_MULTISELECT) {
          this.AUDIO_MULTISELECT_EXIT().then(resolve);
        } else {
          resolve();
        }
      });
    },

    $_performSplitAudio(e) {
      return new Promise((resolve, reject) => {
        this.SPLIT_AUDIO({
          track: this.audioForSplit,
          time: this.secondhand,
        }).then(() => {
          const splitMode = this.MODE_SECONDHAND_CUT ? 'dual' : 'single';
          tmBus.$emit('callbackAudioAssetSplitStart', splitMode);
          tmBus.$emit('callbackChange', { e });
          resolve();
        }, reject);
      });
    },

    $_beforeSplitVisual(e) {
      return new Promise((resolve, reject) => {
        if (this.MODE_SCENE_EDIT) {
          this.SCENE_EDIT_MODE_APPLY({ keepItSelected: true }).then(
            ({ sceneTrack }) => {
              tmBus.$emit('callbackApplySceneEdit', {
                e,
                asset: sceneTrack.data,
                analyticsData: { confirm: 'v', using: 'split' },
              });
              resolve();
            },
            reject
          );
        } else if (this.MODE_AUDIO_MULTISELECT) {
          this.AUDIO_MULTISELECT_EXIT().then(resolve);
        } else {
          resolve();
        }
      });
    },

    $_performSplitVisual(e) {
      return new Promise((resolve, reject) => {
        const track = this.$_time2visualTrack(this.secondhand);
        tmBus.$emit('callbackAssetSplitStart', track.data);
        this.SPLIT_VISUAL({ track, time: this.secondhand }).then((context) => {
          tmBus.$emit('callbackAssetSplitStop', { e, ...context });
          resolve();
        }, reject);
      });
    },
  },
};
</script>

<style lang="scss">
$idleColour: $color-pink-l;
$hoverColour: $color-pink-d;
$disableCutColour: #f5b7c7;
$idleUnixColour: $color-normal;
$hoverUnixColour: $color-black;
$disableUnixColour: #afafaf;

.tm-timemachine > .tm-secondhand {
  --panelH: 24px;
  --bubbleTop: 5px;
  --triangleTop: calc(var(--bubbleTop) + 12px);
  --bodyColour: #{$idleColour};

  position: absolute;
  top: 0;
  width: 1px;
  height: var(--tm-pxRowHeight);
  z-index: var(--tm-zPlayhead);
  pointer-events: none;

  &.-hovered {
    --bodyColour: #{$hoverColour};
  }

  .-drag-handle {
    cursor: col-resize;
    pointer-events: auto;
  }

  .-leveler-box {
    margin-top: var(--tm-pxRowBlankTop);

    .-line {
      border: 1px solid var(--bodyColour);

      &.-cut {
        border-style: dashed;
      }

      &.-visual-track {
        height: var(--tm-pxAssetHeight);
      }

      &.-text-track {
        height: var(--tm-pxTranscriptTrackH);
      }

      &.-audio-track {
        height: var(--tm-pxAudioTrackH);
      }

      &.-panel-padding {
        height: 6px;
      }
    }
  }

  .-bubble {
    position: absolute;
    width: 16px;
    height: 16px;
    border-radius: 8px;
    background-color: var(--bodyColour);
    top: var(--bubbleTop);
    left: 50%;
    transform: translateX(-50%);
  }

  .-triangle {
    @include triangle(14px, 14px, var(--bodyColour), bottom);
    width: 0;
    height: 0;
    top: var(--triangleTop);
    left: 50%;
    transform: translate(-50%);
  }

  .-panel {
    position: absolute;
    display: flex;
    bottom: 0;

    .-banner {
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--tm-pxSecondhandBannerW);
      height: var(--panelH);
      border-radius: 1px;
      background-color: var(--bodyColour);

      .-text {
        color: #fff;
        font-size: 12px;
        line-height: 0;
        @include user-select(none);
      }
    }

    .-trailer {
      margin-left: 1px;
      width: var(--panelH);
      display: flex;
    }

    .-button {
      color: white;
      height: var(--panelH);
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: default;
      visibility: visible;
      opacity: 1;
      transition: opacity 0.12s ease;
      pointer-events: auto;

      &.e {
        cursor: pointer;
      }

      &.-unix {
        border-radius: 1px;
        width: 100%;
        justify-content: center;
        background-color: $disableUnixColour;

        .wticon {
          font-size: 14px;
        }

        &.e:not(.x) {
          background-color: $idleUnixColour;
          &:hover {
            background-color: $hoverUnixColour;
          }
        }

        &.x {
          background-color: var(--tm-floorBackground);
          color: $color-normal;
          &:hover {
            color: $color-black;
          }
        }
      }

      &.-visual {
        background-color: $disableCutColour;
        border-radius: 1px 0 0 1px;
        width: 30px;
        border-right: 2px solid var(--tm-floorBackground);

        .wticon {
          font-size: 16px;
        }

        &.e {
          background-color: $idleColour;
          &:hover {
            background-color: $hoverColour;
          }
        }
      }

      &.-audio {
        background-color: $disableCutColour;
        border-radius: 0 1px 1px 0;
        width: 28px;

        .wticon {
          font-size: 15px;
          transform: translateX(-0.5px);
        }

        &.e {
          background-color: $idleColour;
          &:hover {
            background-color: $hoverColour;
          }
        }
      }
    }
  }
}
</style>
